import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Smartphone, Mail, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "../../components/auth/AuthLayout";
import { ResendCountdown } from "../../components/auth/ResendCountdown";
import { OtpBoxes } from "../../components/auth/OtpBoxes";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { authApi, errorMessage } from "../../lib/api";

const PENDING_KEY = "kt_pending_signup";
export const savePendingSignup = (p) => sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));

// Step 2 of signup: the backend holds a pending account until a code sent to the phone OR email is verified.
export default function SignupVerify() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending] = useState(() => { try { return location.state?.pending_id ? location.state : JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null"); } catch { return null; } });
  const [channel] = useState("email");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState({ phone: false, email: false });
  const [phoneDone, setPhoneDone] = useState(false);
  const [potp, setPotp] = useState("");

  const verifyPhone = async (e) => {
    e?.preventDefault();
    if (potp.length < 4) return toast.error("Enter the code we sent you");
    setBusy(true);
    try {
      const res = await authApi.signupVerifyPhone({ pending_id: pending.pending_id, otp: potp.trim() });
      if (res?.complete && res?.token) {
        // Phone-only account (no email) — activated immediately.
        sessionStorage.removeItem(PENDING_KEY);
        const user = applyAuth(res);
        toast.success("Verified. Welcome to KohimaTaxi!");
        navigate(homeForRole(user?.role), { replace: true });
        return;
      }
      setPhoneDone(true); setPotp("");
      toast.success("Phone verified — now confirm your email");
    } catch (err) { toast.error(errorMessage(err, "Invalid code")); } finally { setBusy(false); }
  };
  const resendPhone = async () => {
    setBusy(true);
    try { await authApi.signupResendPhone({ pending_id: pending.pending_id }); setPotp(""); toast.success("New WhatsApp code sent"); }
    catch (err) { toast.error(errorMessage(err, "Could not resend")); } finally { setBusy(false); }
  };

  const autoVerifyRef = useRef("");
  useEffect(() => {
    if (pending?.pending_id && otp.length === 6 && !busy && autoVerifyRef.current !== otp) { autoVerifyRef.current = otp; verify(); }
  }, [otp, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!pending?.pending_id) {
    return (
      <AuthLayout title="Nothing to verify" subtitle="Start by creating your account." footer={<Link to="/auth/signup" className="font-semibold text-brand hover:underline" data-testid="signup-verify-restart">Go to sign up</Link>}>
        <p className="text-sm text-ink-soft" data-testid="signup-verify-empty">Your sign-up session has expired or was not started on this device.</p>
      </AuthLayout>
    );
  }

  const target = pending.email_masked;

  if (pending.phone_verification_required && !phoneDone) {
    return (
      <AuthLayout
        title="Verify your phone"
        subtitle={`We sent a 6-digit code to ${pending.phone_masked} by WhatsApp and SMS. Verify it to activate your account.`}
        footer={<>Wrong details? <Link to="/auth/signup" onClick={() => sessionStorage.removeItem(PENDING_KEY)} className="font-semibold text-brand hover:underline" data-testid="signup-verify-restart">Start over</Link></>}
      >
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-brand-700" data-testid="signup-phone-note"><Smartphone className="mt-0.5 h-4 w-4 shrink-0" /> We verify your phone so we can reach you about your rides and orders.{pending.email_provided ? " You'll confirm your email next." : ""}</div>
        <form onSubmit={verifyPhone} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="sv-potp">Verification code</Label>
            <OtpBoxes value={potp} onChange={(v) => setPotp(v)} autoFocus testId="signup-phone-otp" />
          </div>
          <Button type="submit" disabled={busy || potp.length < 4} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="signup-phone-verify-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify phone</Button>
          <ResendCountdown onResend={resendPhone} busy={busy} nonce={`phone-${nonce}`} testId="signup-phone-resend" />
        </form>
      </AuthLayout>
    );
  }

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.length < 4) return toast.error("Enter the code we sent you");
    setBusy(true);
    try {
      const data = await authApi.signupVerify({ pending_id: pending.pending_id, channel: "email", otp: otp.trim() });
      sessionStorage.removeItem(PENDING_KEY);
      const user = applyAuth(data);
      toast.success("Verified. Welcome to KohimaTaxi!");
      navigate(homeForRole(user?.role), { replace: true });
    } catch (err) {
      const n = attempts + 1; setAttempts(n);
      toast.error(errorMessage(err, "Invalid code"));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try { await authApi.signupResend({ pending_id: pending.pending_id, channel: "email" }); setNonce((n) => n + 1); setOtp(""); setAttempts(0); toast.success(`New code sent to ${target}`); }
    catch (err) { toast.error(errorMessage(err, "Could not resend")); } finally { setBusy(false); }
  };

  return (
    <AuthLayout
      title="Verify your email"
      subtitle="We sent a 6-digit code to your email. Enter it below to activate your account."
      footer={<>Wrong details? <Link to="/auth/signup" onClick={() => sessionStorage.removeItem(PENDING_KEY)} className="font-semibold text-brand hover:underline" data-testid="signup-verify-restart">Start over</Link></>}
    >
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-brand-700" data-testid="signup-verify-note"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> Your account stays locked until this code is confirmed — logins are refused until then.</div>
      <form onSubmit={verify} className="space-y-5">
        <p className="text-sm text-ink-soft" data-testid="signup-verify-sent-to">Code sent to <span className="font-semibold text-ink">{target}</span>. <span className="ml-1" data-testid="signup-verify-spam">Not in your inbox? Check your spam or junk folder.</span>{pending.email_delivered === false && <span className="ml-1 text-red-600" data-testid="signup-verify-undelivered">Delivery failed — use Resend.</span>}</p>
        <div className="space-y-2">
          <Label htmlFor="sv-otp">6-digit code</Label>
          <OtpBoxes value={otp} onChange={(v) => setOtp(v)} autoFocus testId="signup-verify-otp" />
          <p className="text-center text-xs text-ink-muted" data-testid="signup-paste-hint">Tip: you can paste the code — it verifies automatically.</p>
          {attempts > 0 && <p className="text-xs text-red-600" data-testid="signup-verify-attempts">{attempts} wrong attempt{attempts > 1 ? "s" : ""}. Check the latest code or request a new one.</p>}
        </div>
        <Button type="submit" disabled={busy || otp.length < 4} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="signup-verify-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify and activate</Button>
        <ResendCountdown onResend={resend} busy={busy} nonce={`email-${nonce}`} testId="signup-verify-resend" />
      </form>
    </AuthLayout>
  );
}
